import { Link, useNavigate, Button, Badge, Card, CardContent, Check, useAuth, ArrowRight } from "@/pages/shared";
import { Helmet } from "react-helmet-async";
import { CLAIMS } from "@/config/claims";

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const b = CLAIMS.billing;
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>Pricing — Creatara plans, exact allowances & billing terms</title>
        <meta name="description" content={`Free: ${CLAIMS.freeAllowance.generations} generations/month. Pro £29/mo. Enterprise £99/mo. Exact entitlements, tax treatment, cancellation and refund policy.`} />
        <link rel="canonical" href="https://creatara.ai/pricing" />
      </Helmet>

      <nav className="border-b border-white/5 sticky top-0 z-40 bg-background/80 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold">Creatara</Link>
          <Link to={user ? "/dashboard" : "/register"}><Button className="bg-violet-600 hover:bg-violet-700 rounded-full">{user ? "Dashboard" : "Start free"}</Button></Link>
        </div>
      </nav>

      <section className="pt-16 pb-10 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <Badge className="mb-4 bg-violet-600/10 text-violet-500 border-violet-600/20">Pricing</Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Simple, exact pricing</h1>
          <p className="text-muted-foreground">Every number below is what the app actually enforces. No asterisks.</p>
        </div>
      </section>

      <section className="px-6 pb-12">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {CLAIMS.plans.map((tier) => (
            <Card key={tier.id} className={`glass h-full ${tier.highlight ? "border-violet-500/50 relative" : ""}`} data-testid={`pricing-page-card-${tier.id}`}>
              {tier.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><Badge className="bg-violet-600 text-white">Most Popular</Badge></div>}
              <CardContent className="p-6">
                <h3 className="text-2xl font-bold mb-2">{tier.name}</h3>
                <div className="flex items-baseline gap-1 mb-6"><span className="text-4xl font-bold">{tier.price}</span><span className="text-muted-foreground">{tier.period}</span></div>
                <ul className="space-y-3 mb-6">
                  {tier.entitlements.map((f, j) => <li key={j} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />{f}</li>)}
                </ul>
                <Button onClick={() => navigate(user ? "/dashboard/settings" : "/register")} className={`w-full rounded-full ${tier.highlight ? "btn-primary-glow bg-violet-600 hover:bg-violet-700" : "bg-white/10 hover:bg-white/20"}`} data-testid={`pricing-page-cta-${tier.id}`}>
                  {user ? "Manage plan" : tier.id === "free" ? "Start free" : "Get started"}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="px-6 pb-12">
        <div className="max-w-3xl mx-auto">
          <Card className="glass" data-testid="pricing-explainer-card">
            <CardContent className="p-6 space-y-3 text-sm text-muted-foreground leading-relaxed">
              <h2 className="text-lg font-semibold text-foreground">What counts as a generation?</h2>
              <p>{CLAIMS.generationDefinition}</p>
              <p><strong className="text-foreground">Reset:</strong> {CLAIMS.freeAllowance.resetRule}.</p>
              <p><strong className="text-foreground">Referral credits:</strong> {CLAIMS.referral.mechanics}</p>
              <p><strong className="text-foreground">When credits run out:</strong> {CLAIMS.creditsRunOut}</p>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="px-6 pb-20">
        <div className="max-w-3xl mx-auto">
          <Card className="glass" data-testid="pricing-billing-terms-card">
            <CardContent className="p-6 space-y-3 text-sm text-muted-foreground leading-relaxed">
              <h2 className="text-lg font-semibold text-foreground">Billing terms</h2>
              <p><strong className="text-foreground">Currency & tax:</strong> {b.currency} {b.tax}</p>
              <p><strong className="text-foreground">Billing cycle:</strong> {b.cycle}</p>
              <p><strong className="text-foreground">Cancellation:</strong> {b.cancel}</p>
              <p><strong className="text-foreground">Refunds:</strong> {b.refunds}</p>
              <p><strong className="text-foreground">After cancellation:</strong> {b.afterCancellation}</p>
            </CardContent>
          </Card>
          <div className="text-center mt-10">
            <Link to={user ? "/dashboard" : "/register"}>
              <Button size="lg" className="bg-violet-600 hover:bg-violet-700 rounded-full px-8" data-testid="pricing-bottom-cta">
                {user ? "Go to Dashboard" : "Start with the Free plan"} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="text-xs text-muted-foreground mt-3">No credit card required on Free. See exactly what publishes where on the <Link to="/integrations" className="underline text-violet-400">integration matrix</Link>.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
